import ComputerIcon from "@mui/icons-material/Computer";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PhonelinkEraseIcon from "@mui/icons-material/PhonelinkErase";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import TabletIcon from "@mui/icons-material/Tablet";
import { IconButton, Stack, Tab, Tabs, Tooltip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import EditorVisualTextField from "developer/edit/components/EditorVisualTextField";
import useColumnEdited from "developer/edit/hooks/useColumnEdited";
import useDynamicField from "developer/edit/hooks/useDynamicField";
import { changeLayoutElementProp } from "developer/edit/store/editorSlice";
import { isObject } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FieldRenderProps } from "react-final-form";
import { useAppDispatch } from "store/hooks";
import DynamicField from "../DynamicField";

const breakpoints = [
  {
    label: "Phone",
    breakpoint: "sm",
    icon: <PhoneIphoneIcon />,
    tabValue: 0,
  },
  {
    label: "Tablet",
    breakpoint: "md",
    icon: <TabletIcon />,
    tabValue: 1,
  },
  {
    label: "Desktop",
    breakpoint: "lg",
    icon: <ComputerIcon />,
    tabValue: 2,
  },
];

const SizeField = () => {
  const [tabSelected, setTabSelected] = useState<number | null>(null);
  const dispatch = useAppDispatch();
  const columnEdited = useColumnEdited();

  const {
    input: { value: size },
  } = useDynamicField<any>("element.size");

  const breakpointSelected = useMemo(() => {
    return breakpoints.find((x) => x.tabValue === tabSelected);
  }, [tabSelected]);

  const isBreakpointsEnabled = useMemo(
    () => (isObject(size) ? true : false),
    [size]
  );

  const name = useMemo(() => {
    return breakpointSelected
      ? `element.size.${breakpointSelected.breakpoint}`
      : "element.size";
  }, [breakpointSelected]);

  useEffect(() => {
    if (isBreakpointsEnabled) {
      setTabSelected(breakpoints[0].tabValue);
    }
  }, [isBreakpointsEnabled]);

  const handleToggleBreakpoints = useCallback(() => {
    const targetProp = columnEdited
      ? `listFields.fields.${columnEdited.index}.size`
      : "size";

    if (isBreakpointsEnabled) {
      const newValue = size?.sm ?? size?.md ?? size?.lg ?? 12;
      setTabSelected(null);
      dispatch(changeLayoutElementProp({ targetProp, value: newValue }));
    } else {
      const newValue = size ?? 12;
      setTabSelected(breakpoints[0].tabValue);
      dispatch(
        changeLayoutElementProp({ targetProp, value: { sm: newValue } })
      );
    }
  }, [columnEdited, dispatch, isBreakpointsEnabled, size]);

  const handleChangeTabSelected = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setTabSelected(newValue);
    },
    []
  );

  const AntTabs = useCallback(() => {
    if (!isBreakpointsEnabled) return null;
    return (
      <Tabs
        value={tabSelected}
        onChange={handleChangeTabSelected}
        aria-label="breakpoints tab selected"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 24, fontSize: 12 }}
      >
        {breakpoints.map((tab) => (
          <Tab
            key={tab.breakpoint}
            icon={tab.icon}
            label={tab.label}
            aria-label={tab.label}
            iconPosition="start"
            sx={{ textTransform: "none", minHeight: 35, height: 35 }}
          />
        ))}
      </Tabs>
    );
  }, [handleChangeTabSelected, isBreakpointsEnabled, tabSelected]);

  const ToggleBreakpoints = useCallback(() => {
    return (
      <Tooltip title={`${isBreakpointsEnabled ? "Remove" : "Add"} Breakpoints`}>
        <IconButton
          size="small"
          onClick={handleToggleBreakpoints}
          sx={{
            color: isBreakpointsEnabled ? "error.light" : undefined,
          }}
        >
          {isBreakpointsEnabled ? <PhonelinkEraseIcon /> : <SmartphoneIcon />}
        </IconButton>
      </Tooltip>
    );
  }, [handleToggleBreakpoints, isBreakpointsEnabled]);

  const SizeInput = useCallback(
    (props: FieldRenderProps<any>) => {
      let placeholder: string | undefined = undefined;
      const breakpoint = breakpointSelected?.breakpoint;

      if (breakpoint === "lg" && size["md"]) {
        placeholder = size["md"].toString();
      } else if (breakpoint === "lg" && size["sm"]) {
        placeholder = size["sm"].toString();
      } else if (breakpoint === "lg" && size["lg"]) {
        placeholder = size["lg"].toString();
      } else if (breakpoint === "md" && size["sm"]) {
        placeholder = size["sm"].toString();
      }

      return (
        <Autocomplete
          id="size"
          size="small"
          onChange={(_event, newValue: number | null) => {
            props.input.onChange({
              target: { value: newValue ?? undefined },
            });
          }}
          value={
            tabSelected
              ? size[breakpointSelected?.breakpoint ?? ""]
              : props.input.value
          }
          options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
          fullWidth
          autoHighlight
          getOptionLabel={(option) => option.toString()}
          isOptionEqualToValue={(option, value) => +option === +value}
          renderInput={(params) => (
            <EditorVisualTextField
              {...params}
              name={props.name}
              label="Size"
              placeholder={placeholder}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />
      );
    },
    [breakpointSelected, size, tabSelected]
  );

  return (
    <DynamicField name={name}>
      {(props) => (
        <Stack spacing={1} mt={1}>
          <AntTabs />
          <Stack direction="row" spacing={1} alignItems="center">
            <SizeInput {...props} />
            <ToggleBreakpoints />
          </Stack>
        </Stack>
      )}
    </DynamicField>
  );
};

export default SizeField;
